import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already logged in on app start
    const initializeAuth = () => {
      try {
        // Use authService.isAuthenticated() which checks token expiration
        if (authService.isAuthenticated()) {
          const userData = authService.getCurrentUser();
          
          if (userData) {
            setUser(userData);
            setIsAuthenticated(true);
          } else {
            // If no user data but token exists, clear everything
            authService.logout();
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear invalid data
        authService.logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      // console.log('AuthContext: Starting login process');
      const response = await authService.login(email, password);
      // console.log('AuthContext: Login API response:', response);
      
      // 토큰이 성공적으로 저장되었는지 확인
      const token = authService.getToken();
      // console.log('AuthContext: Token retrieved:', !!token);
      
      if (!token) {
        throw new Error('Token not saved properly');
      }
      
      const userData = authService.getCurrentUser();
      // console.log('AuthContext: User data retrieved:', userData);
      
      setUser(userData || { email });
      setIsAuthenticated(true);
      
      return response;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      throw error;
    }
  };

  const googleLogin = async (accessToken) => {
    try {
      const response = await authService.googleLogin(accessToken);
      
      setUser(response.user);
      setIsAuthenticated(true);
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    googleLogin,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
